import React, {useState} from 'react';
import {Collapse, Grid, InputLabel, MenuItem,} from '@mui/material';
import {LocalDocumentsSearchQueryParams} from "../../../../../models/local-documents.models";
import {
    FiltersFieldsWrapper,
    FiltersLabel,
    FiltersTopRow,
    StyledDatePicker,
    StyledFormControl,
    StyledSelect,
    StyledTextField,
} from './DocumentsDigitizationFilters.styled';
import {useTranslation} from "react-i18next";
import SimpleLinkButton from "../../../../../components/documents-digitization/SimpleLinkButton";
import {formatDateToCount, parseDateToCount} from "../../../../../utils/format-date";

interface DocumentsDigitizationFiltersProps {
    visible: boolean;
    filters: LocalDocumentsSearchQueryParams;
    onFiltersChange: (filters: LocalDocumentsSearchQueryParams) => void;
}

const DocumentsDigitizationFilters = ({
    visible,
    filters,
    onFiltersChange,
}: DocumentsDigitizationFiltersProps) => {
    const {t} = useTranslation();
    const [dateError, setDateError] = useState<string | null>(null);

    const STATUS_OPTIONS = [
        { value: '', label: t('documentsDigitization.status.ALL') },
        { value: 'DONE', label: t('documentsDigitization.status.DONE') },
        { value: 'APPROVED', label: t('documentsDigitization.status.APPROVED') },
        { value: 'PROCESSING', label: t('documentsDigitization.status.PROCESSING') },
        { value: 'TODO', label: t('documentsDigitization.status.TODO') },
        { value: 'ERROR', label: t('documentsDigitization.status.ERROR') },
        { value: 'ARCHIVED', label: t('documentsDigitization.status.ARCHIVED') },
    ];

    const TYPE_OPTIONS = [
        { value: '', label: t('documentsDigitization.documentType.ALL') },
        { value: 'MEDICAL_HISTORY', label: t('documentsDigitization.documentType.MEDICAL_HISTORY') },
        { value: 'TEST_RESULTS', label: t('documentsDigitization.documentType.TEST_RESULTS') },
        { value: 'REFERRAL', label: t('documentsDigitization.documentType.REFERRAL') },
        { value: 'STATEMENT', label: t('documentsDigitization.documentType.STATEMENT') },
        { value: 'OTHER', label: t('documentsDigitization.documentType.OTHER') },
        { value: 'NOTYPE', label: t('documentsDigitization.documentType.NOTYPE') },
        { value: 'UNRECOGNIZED', label: t('documentsDigitization.documentType.UNRECOGNIZED') },
    ];

    const handleChange = (key: keyof LocalDocumentsSearchQueryParams, value: string) => {
        onFiltersChange({ ...filters, [key]: value || undefined, pageNumber: 0 });
    };

    const handleClear = () => {
        onFiltersChange({ pageNumber: 0, pageSize: filters.pageSize });
        setDateError(null);
    };


    const handleDateChange = (key: 'dateFrom' | 'dateTo', value: Date | null) => {
        const newDate = value && !isNaN(value.getTime())
            ? formatDateToCount(value)
            : undefined;

        const newFilters = {
            ...filters,
            [key]: newDate,
            pageNumber: 0,
        }

        // validation
        const fromDate = key === 'dateFrom'
            ? (value ?? null)
            : (filters.dateFrom ? parseDateToCount(filters.dateFrom) : null);

        const toDate = key === 'dateTo'
            ? (value ?? null)
            : (filters.dateTo ? parseDateToCount(filters.dateTo) : null);

        if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
            setDateError(t('documentsDigitization.filters.dateRangeError'));
        } else {
            setDateError(null);
            onFiltersChange(newFilters);
        }
    };

    return (
        <Collapse in={visible} sx={{width: '100%'}}>
            <FiltersFieldsWrapper>
                <FiltersTopRow>
                    <FiltersLabel>
                        {t('documentsDigitization.filters.label')}
                    </FiltersLabel>

                    <SimpleLinkButton onClick={handleClear}>
                        {t('documentsDigitization.filters.clearButton')}
                    </SimpleLinkButton>
                </FiltersTopRow>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg>
                        <StyledDatePicker
                            label={t('documentsDigitization.filters.dateFromLabel')}
                            value={filters.dateFrom ? new Date(filters.dateFrom) : null}
                            onChange={(val) => handleDateChange('dateFrom', val)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: !!dateError,
                                    helperText: dateError ?? ' ',
                                } as any,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg>
                        <StyledDatePicker
                            label={t('documentsDigitization.filters.dateToLabel')}
                            value={filters.dateTo ? new Date(filters.dateTo) : null}
                            onChange={(val) => handleDateChange('dateTo', val)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: !!dateError,
                                    helperText: dateError ?? ' ',
                                } as any,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg>
                        <StyledFormControl  fullWidth size="small">
                            <InputLabel>{t('documentsDigitization.filters.statusLabel')}</InputLabel>
                            <StyledSelect
                                label={t('documentsDigitization.filters.statusLabel')}
                                value={filters.status ?? ''}
                                onChange={e => handleChange('status', e.target.value as string)}
                                fullWidth
                                size="small"
                            >
                                {STATUS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </StyledFormControl >
                    </Grid>

                    <Grid item xs={12} md={6} lg>
                        <StyledFormControl fullWidth size="small">
                            <InputLabel>{t('documentsDigitization.filters.typeLabel')}</InputLabel>
                            <StyledSelect
                                label={t('documentsDigitization.filters.typeLabel')}
                                value={filters.type ?? ''}
                                onChange={e => handleChange('type', e.target.value as string)}
                                fullWidth
                                size="small"
                            >
                                {TYPE_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </StyledFormControl >
                    </Grid>
                </Grid>
            </FiltersFieldsWrapper>
        </Collapse>
    );
};

export default DocumentsDigitizationFilters;